import React from 'react';
import styled from 'styled-components';
import {
  FlexColumn,
  FlexRow,
  formatHash,
  HashLength,
  PageTile,
} from '@make-software/cspr-design';
import { BodyText, Tooltip } from '@block-explorer/ui';
import {
  AccountInfoResult,
  NFTTokenMetadata,
  NFTTokenMetadataEntry,
  NFTTokenResult,
} from '@block-explorer/entities';
import { Trans, useTranslation } from 'react-i18next';
import { getNftTokenDetails } from '../../utils/nft-token-metadata';
import { buildRouterPath } from '../../router/utils';
import { NFT_TOKEN_PATH } from '../../router/paths';
import { HistoryLink } from '../history-link/history-link';
import { NftTokenCard } from '../../scenes/NftTokenDetailsScene/components/NftTokenCard';
import { ContractIdentifier } from '../contract-identifier/contract-identifier';
import {
  SHORTENED_CONTRACT_NAME_LENGTH,
  truncateName,
} from '../action-messages/utils';
import { mapToDictionary } from '../../utils/object';
import TransactorInfo from '../transactor-info/transactor-info';
import CopyButton from '../copy-button/copy-button';

const GridTile = styled(PageTile)(({ theme }) =>
  theme.withMedia({
    height: 'auto',
    padding: [12, 16],
  })
);

const NftCardLinkWrapper = styled.div(() => ({
  marginBottom: 20,
  a: {
    display: 'flex',
  },
  '& a > *': {
    width: '100%',
    aspectRatio: '1 / 1',
  },
}));

const StyledFlexColumn = styled(FlexColumn)(({ theme }) => ({
  '> * + *': {
    borderTop: theme.border.separator,
    marginTop: 8,
    paddingTop: 8,
  },
}));

const StyledBodyText = styled(BodyText)(({ theme }) => ({
  lineHeight: '28px',
}));

export interface NftTileProps {
  token: NFTTokenResult;
  getAccountInfo: <T>(publicKey: string) => T | null | undefined;
  showOwnerInfo?: boolean;
  showContractPackageInfo?: boolean;
}

export const NftTile = ({
  token,
  getAccountInfo,
  showOwnerInfo = false,
  showContractPackageInfo = true,
}: NftTileProps) => {
  const { t } = useTranslation();

  const { itemsWithLinks: nftTokenMetadataWithLinks } =
    getNftTokenDetails(token);
  const metadataKeyValue = !nftTokenMetadataWithLinks
    ? null
    : mapToDictionary<NFTTokenMetadata, NFTTokenMetadataEntry>(
        nftTokenMetadataWithLinks
      );
  const name = metadataKeyValue?.name || '-';
  const tokenPath = buildRouterPath(
    NFT_TOKEN_PATH,
    {
      contract_package_hash: token.contract_package_hash,
      token_id: token.token_id,
    },
    undefined
  );

  const accountInfo = getAccountInfo<AccountInfoResult>(
    token?.owner_public_key
  );
  const csprName = accountInfo?.cspr_name;

  return (
    <GridTile>
      <NftCardLinkWrapper>
        <HistoryLink
          href={tokenPath}
          ariaDescription={'Link to nft token details'}
        >
          <NftTokenCard nftTokenMetadataWithLinks={nftTokenMetadataWithLinks} />
        </HistoryLink>
      </NftCardLinkWrapper>
      <StyledFlexColumn>
        <Tooltip tooltipContent={name} caption={t('Token name')}>
          <StyledBodyText
            size={1}
            scale={'xl'}
            lineHeight={'sm'}
            variation={'black'}
          >
            {truncateName(name, SHORTENED_CONTRACT_NAME_LENGTH)}
          </StyledBodyText>
        </Tooltip>
        {showOwnerInfo ? (
          <FlexColumn gap={6}>
            <BodyText
              size={3}
              scale={'xs'}
              lineHeight={'sm'}
              variation={'darkGray'}
            >
              <Trans t={t}>Owner</Trans>
            </BodyText>
            <TransactorInfo
              type={token?.owner_type}
              publicKey={token?.owner_public_key || token?.owner_hash}
              csprName={token?.owner_cspr_name || csprName}
              loading={!token?.owner_hash}
              hash={token?.owner_hash}
              accountInfo={
                accountInfo?.account_info ||
                accountInfo?.centralized_account_info
              }
              avatarSize="default"
            />
          </FlexColumn>
        ) : null}
        {showContractPackageInfo ? (
          <FlexColumn gap={6}>
            <BodyText
              size={3}
              scale={'xs'}
              lineHeight={'sm'}
              variation={'darkGray'}
            >
              <Trans t={t}>Contract</Trans>
            </BodyText>
            <ContractIdentifier
              contractPackage={token.contract_package}
              hash={token.contract_package_hash}
              withName
            />
          </FlexColumn>
        ) : null}
        <FlexRow gap={6} align={'center'}>
          <BodyText
            size={3}
            scale={'xs'}
            variation={'darkGray'}
            noWrap
            lineHeight={'sm'}
          >
            <Trans t={t}>Token ID</Trans>
          </BodyText>
          <Tooltip tooltipContent={token.token_id} caption={t('Token ID')}>
            <BodyText size={3} scale={'sm'} monotype lineHeight={'sm'}>
              <HistoryLink
                href={tokenPath}
                ariaDescription={'Link to nft token details'}
              >
                {formatHash(token.token_id, HashLength.TINY)}
              </HistoryLink>
            </BodyText>
          </Tooltip>
          <CopyButton value={token.token_id} />
        </FlexRow>
      </StyledFlexColumn>
    </GridTile>
  );
};

export default NftTile;
